import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import EmailField from '@/components/form/email/EmailField';
import PasswordField from '@/components/form/password/PasswordField';
import { Button } from '@/components/buttons/Button';
import Form from '@/components/form/Form';
import {
  HomeHeroAuthenticationStep,
  LoginFlowVariant,
  useHomeHeroAuthenticationStepActions,
} from '@/store/authentication/AuthenticationStore';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess } from '@/store/actions/auth';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import PromptMessage from '@/components/UI/PromptMessage';
import { useRegistration } from '@/hooks/useRegistration';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import StorageManager, { ReturningUserLoginMethod } from '@/shared/storageManager';
import { BtnBlueLink } from '@/components/buttons/BtnBlueLink';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { scrollToTop } from '@/utils/utils';
import { selectIsCanadaMarket, selectLocale } from '@/store/selectors/commonSelectors';
import BffApiService from '@/shared/bffApiService';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { BffAuthResponse, BlockReasonType } from '@lucky7ventures/bff-types';
import { BffApiError } from '@/models/bff-api-error';
import { envIsMexico } from '@/constants/constants';
import { shouldShowBiometricOptIn } from '@/utils/biometric-storage';
import { isBiometricLoginAvailable } from '@/hooks/useBiometricLogin';
import { CustomWebToAppEvents } from '@/models/enum/app-wrapper.enum';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';

const initialValues = {
  email: '',
  password: '',
};

const LoginForm = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { openModal } = useModal();
  const { loading, error, request } = useBffApiRequest<BffAuthResponse>();
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const navigate = useAbsoluteNavigate();
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(null);
  const locale = useSelector(selectLocale);
  const isCanadaMarket = useSelector(selectIsCanadaMarket);
  const { setLoginFlowVariant, setHomeHeroAuthenticationStep, setAuthenticationCredentials } =
    useHomeHeroAuthenticationStepActions();

  useEffect(() => {
    if (isBiometricLoginAvailable()) {
      sendEventToAppWrapper(CustomWebToAppEvents.request_biometric_status);
    }
  }, []);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .email(intl.formatMessage({ id: 'inputs.email.invalid' }))
      .trim(),
    password: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .trim(),
  });

  const forgotPasswordClicked = () => {
    triggerGTag(GTagEvents.forgot_password_click);
    navigate('/forgot-password');
  };

  const successCallback = (data: BffAuthResponse, username: string, password: string) => {
    if (data.blockReason?.blockReasonType === BlockReasonType.SelfExclusion) {
      setErrorMessage(intl.formatMessage({ id: 'login.error.localSelfExclusion' }));
      return;
    }

    if (data.blockReason?.blockReasonType === BlockReasonType.SelfExclusionRegisterMatch) {
      const betGuardUrl = locale === 'ca-fr' ? 'https://garde-jeu.ca/' : 'https://betguard.ca/';
      setErrorMessage(
        <FormattedMessage
          id="login.error.centralSelfExclusion"
          values={{
            a: chunks => (
              <a href={betGuardUrl} target="_blank" rel="noopener noreferrer" className="underline">
                {chunks}
              </a>
            ),
          }}
        />,
      );
      return;
    }

    if (isCanadaMarket && data.blockReason?.blockReasonType === BlockReasonType.Timeout) {
      setErrorMessage(intl.formatMessage({ id: 'login.error.timeout' }));
      return;
    }

    StorageManager.setIsReturningUser(ReturningUserLoginMethod.Email);

    if (data.result === 'SUCCESSFUL') {
      dispatch(loginSuccess(data.token, { blockReason: data.blockReason }));
      triggerGTag(GTagEvents.login_success);

      if (isBiometricLoginAvailable() && shouldShowBiometricOptIn()) {
        openModal(MODALS.BIOMETRIC_OPT_IN, { email: username, password });
      }
    } else if (data.result === 'TWO_FACTOR_FIRST_STEP_SUCCESSFUL') {
      setAuthenticationCredentials(username, password);
      setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.TwoFactorAuthentication);
      triggerGTag(GTagEvents.login_2fa);
    }
  };

  const errorCallback = (error: BffApiError) => {
    setLoginAttempts(prevState => prevState + 1);

    if (loginAttempts > 1) {
      openModal(MODALS.FAILED_LOGIN);
    }

    triggerGTag(GTagEvents.login_error, {
      errorId: error.code,
    });
  };

  const switchToSmsLogin = () => {
    setLoginFlowVariant(LoginFlowVariant.Mobile);
    scrollToTop();
  };

  const handleSubmit = ({ email, password }: { email: string; password: string }) => {
    setErrorMessage(null);
    const payload = { username: email, password };

    request({
      apiMethod: BffApiService.login,
      payload,
      errorCallback: errorCallback,
      successCallback: data => successCallback(data, email, password),
    });
  };

  const openRegistrationClicked = () => {
    openRegistration({ openedFrom: OpenedFrom.LoginFirstStep });
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      className="flex flex-col w-full justify-between gap-7"
    >
      {() => (
        <>
          <EmailField
            data-cy="login-form-email"
            label={intl.formatMessage({ id: 'inputs.email' })}
            name="email"
            className="text-white border-[1px] border-transparent focus-within:border-blue-light bg-white/20"
          />
          <PasswordField
            data-cy="login-form-password"
            label={intl.formatMessage({ id: 'inputs.password' })}
            name="password"
            className="text-white border-[1px] border-transparent focus-within:border-blue-light bg-white/20"
          />
          <div className="grid grid-cols-1 gap-4 mt-4">
            {(error || errorMessage) && (
              <PromptMessage
                type="error"
                message={errorMessage || intl.formatMessage({ id: 'error.1' })}
              />
            )}
            <Button
              text={intl.formatMessage({ id: 'authentication.login.login-button' })}
              type="submit"
              btnType="primary"
              data-cy="login-form-submit-button"
              isLoading={loading}
            />
            <div className="text-center">
              {!envIsMexico() && !isRegistrationRemoved && (
                <div className="text-white text-[15px] mt-6">
                  <FormattedMessage
                    id="authentication.login.change-to-register"
                    values={{
                      a: str => (
                        <button
                          type="button"
                          aria-label="Switch to registration button"
                          data-cy="login-form-switch-registration-button"
                          onClick={openRegistrationClicked}
                          className="text-blue-lighter font-bold underline cursor-pointer focus:border-[1px] focus:border-blue-light"
                        >
                          {str}
                        </button>
                      ),
                    }}
                  />
                </div>
              )}

              <p className="mt-3 block !text-[12px] italic text-white mb-0">
                <FormattedMessage
                  id="authentication.login.forgot-password"
                  values={{
                    a: str => (
                      <button
                        type="button"
                        aria-label="Switch to forgot password form"
                        aria-roledescription="Used for switching to password reset form"
                        data-cy="login-form-switch-forgot-password-button"
                        onClick={forgotPasswordClicked}
                        className="text-blue-lighter font-bold underline cursor-pointer focus:border-[1px] focus:border-blue-light"
                      >
                        {str}
                      </button>
                    ),
                  }}
                />
              </p>
            </div>
            {locale === 'th' && (
              <>
                <div className="h-[1px] bg-white w-full opacity-60"></div>
                <BtnBlueLink
                  data-cy="login-form-switch-login-via-mobile"
                  onClickHandler={switchToSmsLogin}
                  text={intl.formatMessage({ id: 'authentication.login.via-mobile' })}
                />
              </>
            )}
          </div>
        </>
      )}
    </Form>
  );
};

export default LoginForm;
